import { FC, ReactNode } from 'react';
import MaskedInput from 'react-text-mask';
import { Tooltip } from 'react-tooltip';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { Plus, Minus } from 'lucide-react';

import { cn } from 'utils/cn';
import { getClean } from 'hooks/useMaskedInput';

interface Props {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  isBlueBg?: boolean;
  isBlueText?: boolean;
  isGreyText?: boolean;
  redBorder?: boolean;
  integerLimit?: number;
  decimalLimit?: number;
  title?: string | ReactNode;
  titleClassName?: string;
  tooltipId?: string;
  hasTooltip?: string | null | ReactNode;
  maxLength?: number;
  suffix?: string;
  wrapperCn?: string;
  decimalSymbol?: string;
  withButtons?: boolean;
  enforceDecimalPlaces?: boolean;
}

const MaskInput: FC<Props> = ({
  value,
  onChange,
  onBlur,
  placeholder = '',
  disabled = false,
  className = '',
  isBlueBg = false,
  isBlueText = false,
  isGreyText = false,
  redBorder = false,
  integerLimit = 15,
  decimalLimit = 6,
  title = '',
  titleClassName = '',
  tooltipId = '',
  hasTooltip = null,
  maxLength = 100,
  suffix = '',
  wrapperCn = '',
  decimalSymbol = ',',
  withButtons,
  enforceDecimalPlaces = false,
}) => {
  const defaultMaskOptions = {
    prefix: '',
    suffix: suffix,
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: '\'',
    allowDecimal: true,
    decimalSymbol: decimalSymbol,
    decimalLimit: decimalLimit,
    integerLimit: integerLimit,
    allowNegative: true,
    allowLeadingZeroes: false,
  };

  const currencyMask = createNumberMask({
    ...defaultMaskOptions,
  });
 
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (enforceDecimalPlaces && decimalLimit === 2) {
      const cleanValue = getClean(e.target.value);

      if (cleanValue && cleanValue !== '') {
        const num = parseFloat(cleanValue);

        if (!isNaN(num)) {
          const formatted = num.toFixed(2).replace('.', decimalSymbol);
          // Update the parent component with the formatted value
          onChange?.(formatted);

          return;
        }
      }
    }

    onBlur?.(e.target.value);
  };

  return (
    <div className={`relative ${wrapperCn}`}>
      {title && (
        <div
          className={cn(
            'absolute text-secondaryText bg-[#fff] rounded left-6 top-1 text-sm',
            { 'bg-transparent': disabled },
            { 'bg-blue-gradient' : isBlueBg },
            titleClassName,
          )}
          data-tooltip-id={tooltipId}
        >
          {title}
        </div>
      )}
      {hasTooltip && (
        <Tooltip
          id={tooltipId}
          place="bottom-start"
          className="tooltip"
          variant="light"
          noArrow
        >
          {hasTooltip}
        </Tooltip>
      )}
      <MaskedInput
        mask={currencyMask}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`${
          disabled
            ? 'cursor-not-allowed bg-[#EEEEEE]'
            : isBlueBg
              ? 'bg-primitiveBlue'
              : 'bg-inherit'
        } ${
          isBlueText
            ? 'text-primary'
            : isGreyText
              ? 'text-secondaryText'
              : 'text-black'
        } p-2.5 text-base rounded-xl border ${
          redBorder
            ? 'border-red500 focus:border-red500 focus:outline-none active:border-red500'
            : 'border-[#CBCBCB] focus:border-[#007AC1] focus:outline-none active:border-[#007AC1]'
        } h-[48px]   ${className}`}
        disabled={disabled}
        maxLength={maxLength}
      />
      {withButtons && (
        <div className='top-1/2 right-2 transform -translate-y-1/2 md:right-2 absolute inline-flex'>
          <div
            onClick={() => value && onChange?.(String((Number(getClean(value)) + 0.01).toFixed(2)))}
            className="p-1 hover:bg-gray-100 text-gray-500 rounded-md hover:text-black cursor-pointer"
          >
            <Plus className="transition-all" size={16} strokeWidth={2.5} />
          </div>
          <div
            onClick={() => value && onChange?.(String((Number(getClean(value)) - 0.01).toFixed(2)))}
            className="p-1 hover:bg-gray-100 text-gray-500 rounded-md hover:text-black cursor-pointer"
          >
            <Minus className={'transition-all'} size={16} strokeWidth={2.5} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MaskInput;
